import { useEffect, useState } from 'react';

import { faArrowLeft, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ModeSelector from './components/ModeSelector';
import QuestionInput from './components/QuestionInput';
import QuestionSidebar from './components/QuestionSidebar';
import { ChecklistStep } from '../../../../../../../backend/src/types/types';
import { useGetCase } from '../../../../../api/queries/useGetCase';
import useGetUser from '../../../../../api/queries/useGetUser';
import CaseAssistantOnboarding from '../components/CaseAssistantOnboarding';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { DocIds } from '@/api/types/backend-dto-types';
import { DraftType, ExecuteKimSearch, KimMode } from '@/types';

interface QuestionProps {
  caseId: string;
  executeSearch: ExecuteKimSearch;
  setQuestionValue: (v: string) => void;
  isCreatingThread: boolean;
  questionValue: string;
  docs: DocIds[];
  taggedDocuments: { doc_id: string; file_name: string }[];
  setTaggedDocuments: (v: { doc_id: string; file_name: string }[]) => void;
  mode: KimMode;
  setMode: (v: KimMode) => void;
  goToThread: (thread_id: string) => void;
  draftType: DraftType | null;
  setDraftType: (draftType: DraftType | null) => void;
  onStartManualAnswer: () => void;
}

const getTitleFromMode = (mode: KimMode) => {
  switch (mode) {
    case KimMode.QuestionFlow:
      return 'Ask a question...';
    case KimMode.ExtractionFlow:
      return 'Extract information...';
    case KimMode.DraftFlow:
      return 'Draft me a…';
  }
};

const Question = ({
  caseId,
  executeSearch,
  isCreatingThread,
  setQuestionValue,
  questionValue,
  docs,
  taggedDocuments,
  setTaggedDocuments,
  mode,
  setMode,
  goToThread,
  draftType,
  setDraftType,
  onStartManualAnswer,
}: QuestionProps) => {
  const [placeholder, setPlaceholder] = useState('Tell me about X...');
  const [showOnboarding, setShowOnboarding] = useState(true);
  const { data: caseData } = useGetCase(caseId ?? '');
  const { data: user } = useGetUser();

  const hasCompletedKimOnboarding = user && !user.onboardingChecklistItems?.includes(ChecklistStep.UseKim);

  useEffect(() => {
    if (user && hasCompletedKimOnboarding) {
      setShowOnboarding(false);
    }
  }, [user, hasCompletedKimOnboarding]);

  return (
    <div className="relative flex h-full w-full">
      <div className="flex h-full w-full items-center">
        {hasCompletedKimOnboarding && <QuestionSidebar taggedDocuments={taggedDocuments} goToThread={goToThread} />}

        {hasCompletedKimOnboarding && user?.organisation?.isLabellingOrg && (
          <div className="absolute top-4 right-2 flex gap-2">
            <button
              onClick={onStartManualAnswer}
              className="flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
            >
              <span>+ Create Manual Answer</span>
            </button>
            <button
              onClick={() => {
                if (!showOnboarding) {
                  trackCustomEvent(TrackingEvents.KimClickHelp);
                }
                setShowOnboarding(!showOnboarding);
              }}
              className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
            >
              {showOnboarding ? (
                <FontAwesomeIcon icon={faArrowLeft} className="text-brandSecondary" />
              ) : (
                <FontAwesomeIcon icon={faQuestionCircle} className="text-brandSecondary" />
              )}

              <span>{showOnboarding ? 'Back' : 'Need some help?'}</span>
            </button>
          </div>
        )}

        <div className="flex w-full flex-col items-center justify-center">
          {/* Show onboarding in center if user hasn't completed any Kim items yet */}
          {user && showOnboarding ? (
            <CaseAssistantOnboarding
              executeSearch={executeSearch}
              setMode={setMode}
              setDraftType={setDraftType}
              setQuestionValue={setQuestionValue}
              caseData={caseData}
            />
          ) : (
            <>
              <div className="flex w-4/5 max-w-4xl flex-col items-center xl:w-3/4">
                <div className="mb-8 text-2xl font-bold text-gray-600">{getTitleFromMode(mode)}</div>

                <QuestionInput
                  onExecuteCall={executeSearch}
                  onChange={(v: string) => setQuestionValue(v)}
                  value={questionValue}
                  docs={docs}
                  placeholder={placeholder}
                  taggedDocuments={taggedDocuments}
                  setTaggedDocuments={setTaggedDocuments}
                  draftType={draftType}
                  setDraftType={setDraftType}
                  mode={mode}
                  isCreatingThread={isCreatingThread}
                  jurisdiction={caseData?.jurisdiction ?? []}
                />
                <ModeSelector mode={mode} setMode={setMode} setPlaceholder={setPlaceholder} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Question;
